import { ChevronDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { AuditEntryDetailDto, AuditEntryDto } from "~/lib/audit";
import { fetchSessionAuditEntry, fetchSessionAuditPage } from "~/lib/audit";
import NotFoundRoute from "~/routes/not-found";
import { useSession } from "~/session/session-context";

import { dayKey, formatAbsoluteTime, formatDayLabel, formatRelativeTime } from "./format-time";

// Page size: the spec calls for `limit=100`. Kept here rather than threaded
// through every call so the loader and the sentinel agree without a prop.
const PAGE_SIZE = 100;

// V2-E9.2 — debounce the patient-name filter so a refetch fires once the
// doctor pauses typing, not on every keystroke.
const FILTER_DEBOUNCE_MS = 250;

type State =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      entries: AuditEntryDto[];
      nextCursor: string | null;
      // `loadingMore` is distinct from `loading` so the timeline stays on screen
      // while the next page resolves — only the bottom shows a spinner.
      loadingMore: boolean;
    };

/**
 * E8.2 — audit-log viewer. Renders a session's audit trail as a vertical
 * timeline, newest first, grouped by day with a sticky day header. Infinite
 * scroll pulls the next page when an IntersectionObserver sentinel near the
 * bottom comes into view; the API's cursor is opaque (see backend's
 * `cursor.ts`).
 *
 * Each row expands (V2-E9.1, HAN-69) to reveal the full structured detail the
 * list endpoint omits, fetched on demand. A patient-name filter (V2-E9.2, HAN-70)
 * narrows the whole timeline to one patient; PDF export remains v2 — the footer
 * points at that.
 */
export function AuditLogScreen(): JSX.Element {
  const { session } = useSession();
  const sessionId = session.id;

  const [state, setState] = useState<State>({ kind: "loading" });

  // `filterInput` is the controlled text box; `appliedFilter` is the debounced
  // value actually sent to the API. Keeping them separate means typing doesn't
  // refetch on every keystroke — only after a pause.
  const [filterInput, setFilterInput] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  useEffect(() => {
    const trimmed = filterInput.trim();
    if (trimmed === appliedFilter) return;
    const timer = setTimeout(() => setAppliedFilter(trimmed), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filterInput, appliedFilter]);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      setState({ kind: "loading" });
      const result = await fetchSessionAuditPage(sessionId, {
        limit: PAGE_SIZE,
        // Only attach the param when a filter is active so the unfiltered call
        // stays a plain `{ limit }` request.
        ...(appliedFilter ? { patientName: appliedFilter } : {}),
      });
      if (signal.cancelled) return;
      if (result.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (result.status === "error") {
        setState({ kind: "error", message: result.message });
        return;
      }
      setState({
        kind: "ready",
        entries: result.data.entries,
        nextCursor: result.data.nextCursor,
        loadingMore: false,
      });
    },
    // Re-binds when the applied filter changes, which re-runs the effect below
    // and reloads page 1 with the new filter (resetting pagination).
    [sessionId, appliedFilter],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // Mirror the cursor into a ref so `loadMore` can read the latest value
  // without re-binding (which would tear down and re-attach the observer
  // every page).
  useEffect(() => {
    nextCursorRef.current = state.kind === "ready" ? state.nextCursor : null;
  }, [state]);

  // The IntersectionObserver can fire several intersections before React
  // commits the `loadingMore: true` flip, so a state-only guard would let
  // duplicate fetches race. The ref turns the guard into a synchronous fence:
  // a second call sees `inFlight.current === true` and bails immediately.
  const inFlight = useRef(false);
  // Latest cursor mirrored as a ref so `loadMore` doesn't depend on closing
  // over a particular `state` snapshot — useCallback can't have `state` in
  // its deps without rebuilding (and re-installing the observer) every render.
  const nextCursorRef = useRef<string | null>(null);
  // Likewise mirror the applied filter so `loadMore` sends the same filter the
  // current page was loaded with, without re-binding (and re-installing the
  // observer) on every filter change.
  const appliedFilterRef = useRef("");
  useEffect(() => {
    appliedFilterRef.current = appliedFilter;
  }, [appliedFilter]);
  const loadMore = useCallback(async () => {
    if (inFlight.current) return;
    const cursor = nextCursorRef.current;
    if (cursor === null) return;
    inFlight.current = true;
    setState((prev) =>
      prev.kind === "ready" && !prev.loadingMore ? { ...prev, loadingMore: true } : prev,
    );

    const result = await fetchSessionAuditPage(sessionId, {
      limit: PAGE_SIZE,
      before: cursor,
      ...(appliedFilterRef.current ? { patientName: appliedFilterRef.current } : {}),
    });
    inFlight.current = false;
    setState((prev) => {
      if (prev.kind !== "ready") return prev;
      if (result.status === "not-found") {
        // Mid-scroll the caller's access disappeared (membership revoked or
        // session deleted). Fall back to the not-found page so the route
        // matches the initial-load semantics.
        return { kind: "not-found" };
      }
      if (result.status === "error") {
        // Keep what we have on screen and surface the error so the doctor can
        // retry rather than seeing an empty page.
        return { ...prev, loadingMore: false };
      }
      return {
        kind: "ready",
        entries: [...prev.entries, ...result.data.entries],
        nextCursor: result.data.nextCursor,
        loadingMore: false,
      };
    });
  }, [sessionId]);

  // IntersectionObserver sentinel — when it scrolls into view, request the
  // next page. The observer is recreated when `nextCursor` flips so we stop
  // observing once the list is exhausted.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (state.kind !== "ready" || state.nextCursor === null) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: "240px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [state, loadMore]);

  // A missing session is the one full-page replacement — everything else keeps
  // the header and filter bar mounted so the filter input never loses focus
  // mid-type when a refetch flips the body back to a skeleton.
  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  return (
    <div className="space-y-6">
      <Header />
      <PatientNameFilter
        value={filterInput}
        onChange={setFilterInput}
        onClear={() => setFilterInput("")}
      />

      {state.kind === "loading" ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading audit log">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-md border bg-muted/40" />
          ))}
        </div>
      ) : state.kind === "error" ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{state.message}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const signal = { cancelled: false };
                void load(signal);
              }}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : state.entries.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          {appliedFilter
            ? `No audit entries match “${appliedFilter}”.`
            : "No actions recorded yet."}
        </div>
      ) : (
        <>
          <div className="space-y-8">
            {groupByDay(state.entries).map((group) => (
              <section key={group.dayKey} aria-label={group.label}>
                <h2 className="sticky top-0 z-10 -mx-4 border-b border-border bg-background/95 px-4 py-2 text-sm font-semibold tracking-wide text-muted-foreground backdrop-blur sm:-mx-6 sm:px-6">
                  {group.label}
                </h2>
                <ol className="mt-3 space-y-3" data-testid={`day-group-${group.dayKey}`}>
                  {group.entries.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} sessionId={sessionId} />
                  ))}
                </ol>
              </section>
            ))}
          </div>
          <div
            ref={sentinelRef}
            aria-hidden="true"
            data-testid="audit-load-sentinel"
            className="h-1"
          />
          {state.nextCursor !== null ? (
            <div
              className="py-4 text-center text-xs text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              {state.loadingMore ? "Loading more…" : "Scroll for older entries"}
            </div>
          ) : (
            <p className="py-4 text-center text-xs text-muted-foreground">
              You&rsquo;ve reached the start of this audit log.
            </p>
          )}
        </>
      )}

      <ComingSoonFooter />
    </div>
  );
}

/**
 * V2-E9.2 — patient-name filter for the audit timeline. A debounced text box
 * (see {@link FILTER_DEBOUNCE_MS}); the parent owns the value so it can drive
 * the refetch. Labelled for screen readers and offers a clear button once a
 * term is entered.
 */
function PatientNameFilter({
  value,
  onChange,
  onClear,
}: {
  value: string;
  onChange: (next: string) => void;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="relative max-w-sm">
      <Label htmlFor="audit-patient-filter" className="sr-only">
        Filter by patient name
      </Label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        id="audit-patient-filter"
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Filter by patient name"
        autoComplete="off"
        className="pl-9 pr-9"
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClear}
          aria-label="Clear patient name filter"
          className="absolute right-0.5 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <header>
      <h1 className="text-lg font-semibold tracking-tight">Audit log</h1>
      <p className="text-sm text-muted-foreground">
        Every action on this handover, in order. The log is permanent and cannot be edited.
      </p>
    </header>
  );
}

function ComingSoonFooter(): JSX.Element {
  return (
    <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      Coming soon: export.
    </p>
  );
}

// Lazy-loaded state of a single row's structured detail. `idle` until the row
// is first expanded, then `loading` → `ready` | `error`. Cached on the row so
// re-collapsing and re-expanding doesn't refetch.
type DetailState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; data: AuditEntryDetailDto }
  | { kind: "error"; message: string };

function AuditRow({ entry, sessionId }: { entry: AuditEntryDto; sessionId: string }): JSX.Element {
  const absolute = formatAbsoluteTime(entry.occurredAt);
  // The hover tooltip carries the relative phrasing per the spec; `title` is
  // the standard browser hover affordance and reads fine via the keyboard
  // (focusable cells aren't required — the screen reader announces the
  // absolute time, which is the load-bearing piece).
  const relative = formatRelativeTime(entry.occurredAt);

  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<DetailState>({ kind: "idle" });
  const panelId = `audit-detail-${entry.id}`;

  const toggle = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    // Fetch the full payload the first time the row opens; subsequent toggles
    // reuse the cached result (or re-show an error the user can retry by
    // collapsing/re-opening once it's idle again — but we keep it sticky).
    if (next && detail.kind === "idle") {
      setDetail({ kind: "loading" });
      const result = await fetchSessionAuditEntry(sessionId, entry.id);
      if (result.status === "ok") {
        setDetail({ kind: "ready", data: result.data });
      } else if (result.status === "not-found") {
        setDetail({ kind: "error", message: "This entry is no longer available." });
      } else {
        setDetail({ kind: "error", message: result.message });
      }
    }
  }, [expanded, detail.kind, sessionId, entry.id]);

  return (
    <li className="rounded-md border border-border/60">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => void toggle()}
        className="grid w-full grid-cols-[auto_1fr_auto] gap-x-4 gap-y-1 rounded-md px-3 py-2 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[auto_auto_1fr_auto] sm:items-baseline"
      >
        <time
          dateTime={entry.occurredAt}
          title={relative}
          className="font-mono text-xs text-muted-foreground"
        >
          {absolute}
        </time>
        <span className="text-xs font-medium text-muted-foreground sm:min-w-[10ch]">
          {entry.actorName ?? "System"}
        </span>
        <span className="col-span-2 text-sm text-foreground sm:col-span-1">
          {entry.plainEnglish}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`size-4 shrink-0 justify-self-end text-muted-foreground transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded ? (
        <div id={panelId} className="border-t border-border/60 px-3 py-2.5">
          {detail.kind === "loading" ? (
            <p className="text-xs text-muted-foreground" role="status">
              Loading details…
            </p>
          ) : detail.kind === "error" ? (
            <p className="text-xs text-destructive">{detail.message}</p>
          ) : detail.kind === "ready" ? (
            <AuditDetail detail={detail.data.detail} />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Renders the structured `detail` payload of an audit entry as a read-only
 * definition list. The payload is the discriminated event that was recorded;
 * we don't switch on every `kind` (there are 30+) — a generic, recursive
 * renderer covers them all and degrades gracefully if a kind grows new fields.
 * `kind` itself is dropped (it duplicates the row's action), and `{ from, to }`
 * diff pairs (patient.updated / ward.edited `changedFields`) render as
 * "before → after" so the change reads at a glance.
 */
function AuditDetail({ detail }: { detail: Record<string, unknown> }): JSX.Element {
  const keys = Object.keys(detail).filter((k) => k !== "kind");
  if (keys.length === 0) {
    return <p className="text-xs text-muted-foreground">No further detail recorded.</p>;
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
      {keys.map((key) => (
        <div key={key} className="contents">
          <dt className="font-medium text-muted-foreground">{humaniseKey(key)}</dt>
          <dd className="min-w-0 break-words text-foreground">
            <DetailValue value={detail[key]} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function DetailValue({ value, depth = 0 }: { value: unknown; depth?: number }): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return <span>{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">none</span>;
    return (
      <ul className="list-disc space-y-0.5 pl-4">
        {value.map((item, i) => (
          <li key={i}>
            <DetailValue value={item} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // A `{ from, to }` diff pair → "before → after".
    if (isDiffPair(obj)) {
      return (
        <span>
          <DetailValue value={obj.from} depth={depth + 1} />
          <span aria-hidden="true" className="mx-1 text-muted-foreground">
            →
          </span>
          <span className="sr-only"> changed to </span>
          <DetailValue value={obj.to} depth={depth + 1} />
        </span>
      );
    }
    // Avoid unbounded nesting — the recorded payloads are shallow, but guard
    // against a future deeply-nested shape rendering an illegible wall.
    if (depth >= 3) {
      return <span className="text-muted-foreground">{JSON.stringify(value)}</span>;
    }
    const entries = Object.entries(obj);
    if (entries.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <dl className="grid grid-cols-[auto_1fr] gap-x-3">
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{humaniseKey(k)}</dt>
            <dd className="min-w-0 break-words">
              <DetailValue value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span>{String(value)}</span>;
}

/** True for an object whose only keys are `from` and `to` (a diff pair). */
function isDiffPair(obj: Record<string, unknown>): obj is { from: unknown; to: unknown } {
  const keys = Object.keys(obj);
  return keys.length === 2 && "from" in obj && "to" in obj;
}

/** "changedFields" → "Changed fields"; "sbarSituation" → "Sbar situation". */
function humaniseKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface DayGroup {
  dayKey: string;
  label: string;
  entries: AuditEntryDto[];
}

function groupByDay(entries: AuditEntryDto[]): DayGroup[] {
  // Entries arrive newest-first; we preserve that order within each day and
  // emit days in the same first-seen order so the most recent day is first.
  const groups: DayGroup[] = [];
  let current: DayGroup | null = null;
  for (const entry of entries) {
    const key = dayKey(entry.occurredAt);
    if (current === null || current.dayKey !== key) {
      current = { dayKey: key, label: formatDayLabel(entry.occurredAt), entries: [] };
      groups.push(current);
    }
    current.entries.push(entry);
  }
  return groups;
}
